import PublicIcon from '@mui/icons-material/Public';
import classNames from 'classnames';
import { Link } from 'gatsby';
import { REGION, RegionEnum } from 'gatsby-theme-hem-store/src/constants';
import { Dropdown } from 'hem-ui';
import Cookies from 'js-cookie';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import { Phone } from 'lucide-react';
import React from 'react';

import { useContactInfo } from '@/hooks/useContactInfo';

import { AnnouncementBar } from '../AnnouncementBar';
import { ChatAvatars } from './ChatAvatars';
import { FlagIconCa } from './FlagIconCa';
import { FlagIconEu } from './FlagIconEu';
import { FlagIconGb } from './FlagIconGb';
import { FlagIconUs } from './FlagIconUs';

const regions = [
  {
    name: 'Canada',
    region: RegionEnum.Ca,
    icon: <FlagIconCa className="h-4 w-auto rounded-sm" />,
    audience: 'B2C',
    url: 'https://hem.com/en-ca/',
  },
  {
    name: 'United States',
    region: RegionEnum.Us,
    icon: <FlagIconUs className="h-4 w-auto rounded-sm" />,
    audience: 'B2C',
    url: 'https://hem.com/en-us/',
  },
  {
    name: 'United States',
    region: RegionEnum.Us,
    icon: <FlagIconUs className="h-4 w-auto rounded-sm" />,
    audience: 'B2B',
    url: 'https://pro.hem.com/en-us/',
  },
  {
    name: 'United Kingdom',
    region: RegionEnum.Gb,
    icon: <FlagIconGb className="h-4 w-auto rounded-sm" />,
    audience: 'B2C',
    url: 'https://hem.com/en-gb/',
  },
  {
    name: 'United Kingdom',
    region: RegionEnum.Gb,
    icon: <FlagIconGb className="h-4 w-auto rounded-sm" />,
    audience: 'B2B',
    url: 'https://pro.hem.com/en-gb/',
  },
  {
    name: 'Europe',
    region: RegionEnum.Eu,
    icon: <FlagIconEu className="h-4 w-auto rounded-sm" />,
    audience: 'B2C',
    url: 'https://hem.com/en-eu/',
  },
  {
    name: 'Europe',
    region: RegionEnum.Eu,
    icon: <FlagIconEu className="h-4 w-auto rounded-sm" />,
    audience: 'B2B',
    url: 'https://pro.hem.com/en-eu/',
  },
];

export const TopNav: React.FC<{
  announcement: string;
  announcementLoggedIn: string;
  theme: string;
}> = ({ announcement, announcementLoggedIn, theme = 'White' }): JSX.Element => {
  const { phone, phoneRaw } = useContactInfo();

  function openIntercom() {
    if (window.Intercom) {
      window.Intercom('show');
    }
  }

  const regionsByAudience = groupBy(regions, 'audience');

  const handleOnClick = ({
    region,
  }: {
    region: {
      name: string;
      region: RegionEnum;
      icon: React.JSX.Element;
      audience: string;
      url: string;
    };
  }) => {
    // Save the user's preference
    const newRegion = region.url.split('-')[1].replace('/', '');
    Cookies.set('hem-region-default', newRegion, { expires: 365 });

    // Redirect to the new locale
    const urlObject = new URL(region.url);
    const origin = urlObject.origin;
    const pathName = window.location.pathname;
    const searchParams = window.location.search;
    const newUrl = `${origin}${pathName.replace(/en-\w{2}/, `en-${newRegion}`)}${searchParams}`;
    let cleanUrl = newUrl.replace(/([^:]\/)\/+/g, '$1'); // Remove double slashes

    // If the origins are different, append the query parameter
    if (window.location.origin !== origin) {
      const url = new URL(cleanUrl);
      url.searchParams.set('locale', newRegion);
      cleanUrl = url.toString();
    }

    window.location.href = cleanUrl;
  };

  return (
    <aside
      className={classNames('px-4 md:px-8 print:hidden', {
        'bg-white': theme === 'White',
        'bg-blue': theme === 'Blue',
        'bg-red-matte': theme === 'Red',
        'bg-green-matte': theme === 'Green',
        'bg-yellow-matte': theme === 'Yellow',
        'bg-hem-x': theme === 'Hem X',
        'bg-black': theme === 'Black',
      })}
    >
      <div
        className={classNames(
          'grid grid-cols-[auto_1fr] items-center gap-2 py-2 md:grid-cols-[auto_1fr_auto_auto] md:gap-4',
          {
            'border-b': theme === 'White',
            // Override the "color-gray-dark" variable for the current theme to style the dropdown icon.
            '[--color-gray-dark:theme(colors.red-gloss)]': theme === 'Red',
            '[--color-gray-dark:theme(colors.green-gloss)]': theme === 'Green',
            '[--color-gray-dark:theme(colors.black)]': theme === 'Yellow',
            '[--color-gray-dark:theme(colors.white)]':
              theme === 'Blue' || theme === 'Black' || theme === 'Hem X',
          },
        )}
      >
        <Dropdown
          toggleElement={regions.find((r) => r.region === REGION)?.icon}
          aria-label="Switch region"
          width={260}
        >
          <ul>
            <li className="mb-2 px-4 text-sm font-bold">Hem</li>
            {sortBy(regionsByAudience.B2C, 'name').map((r) => (
              <li key={r.name}>
                <button
                  onClick={() => handleOnClick({ region: r })}
                  className="!py-2"
                  title={`Hem ${r.name}`}
                >
                  <span className="flex items-center gap-4 text-sm font-bold leading-none">
                    {r.icon}
                    <span>
                      {r.name}
                      <span className="block text-sm font-normal text-gray-500">
                        {r.url.replace('https://', '').slice(0, -1)}
                      </span>
                    </span>
                  </span>
                </button>
              </li>
            ))}
            <li className="my-2 px-4 text-sm font-bold">Hem Pro</li>
            {sortBy(regionsByAudience.B2B, 'name').map((r) => (
              <li key={r.name}>
                <button
                  onClick={() => handleOnClick({ region: r })}
                  className="!py-2"
                  title={`Hem Pro ${r.name}`}
                >
                  <span className="flex items-center gap-4 text-sm font-bold leading-none">
                    {r.icon}
                    <span>
                      {r.name}
                      <span className="block text-sm font-normal text-gray-500">
                        {r.url.replace('https://', '').slice(0, -1)}
                      </span>
                    </span>
                  </span>
                </button>
              </li>
            ))}
            <li className="my-2 px-4 text-sm font-bold">None of the above?</li>
            <li>
              <Link
                to="/international-retailers"
                className="!py-2"
                title="Hem International resellers"
              >
                <span className="flex items-center gap-4 text-sm font-bold leading-none">
                  <PublicIcon
                    fontSize="small"
                    style={{ fill: 'var(--color-blue)' }}
                  />
                  <span>International resellers</span>
                </span>
              </Link>
            </li>
          </ul>
        </Dropdown>

        <AnnouncementBar {...{ announcement, announcementLoggedIn, theme }} />

        <div
          className={classNames('hidden items-center gap-2 md:flex', {
            'text-black': theme === 'White' || theme === 'Yellow',
            'text-white':
              theme === 'Blue' || theme === 'Black' || theme === 'Hem X',
            'text-red-gloss': theme === 'Red',
            'text-green-gloss': theme === 'Green',
          })}
        >
          <Phone size={16} />
          <a
            className={classNames('text-sm font-bold leading-none', {
              'text-black hover:text-blue hover:no-underline':
                theme === 'White' || theme === 'Yellow',
              'text-white hover:text-white hover:underline':
                theme === 'Blue' || theme === 'Black' || theme === 'Hem X',
              'text-red-gloss hover:text-red-gloss hover:underline':
                theme === 'Red',
              'text-green-gloss hover:text-green-gloss hover:underline':
                theme === 'Green',
            })}
            title={`Need assistance? Call us on ${phoneRaw}.`}
            href={`tel:${phoneRaw}`}
          >
            {phone}
          </a>
        </div>
        <button
          className={classNames('hidden items-center gap-2 md:flex', {
            'text-black hover:text-blue hover:no-underline':
              theme === 'White' || theme === 'Yellow',
            'text-white hover:text-white hover:underline':
              theme === 'Blue' || theme === 'Black' || theme === 'Hem X',
            'text-red-gloss hover:text-red-gloss hover:underline':
              theme === 'Red',
            'text-green-gloss hover:text-green-gloss hover:underline':
              theme === 'Green',
          })}
          onClick={openIntercom}
          aria-label="Need assistance? Chat with an expert."
        >
          <ChatAvatars {...{ theme }} />
          <span className="text-sm font-bold leading-none">
            Chat with an expert
          </span>
        </button>
      </div>
    </aside>
  );
};
